/**
 * /registrar-empresa — Self-service company registration.
 *
 * One server call does it all: POST /auth/me/register-organization runs
 * a single cross-schema transaction that creates the organization, the
 * OWNER membership and the matching taxor.clients row. The frontend
 * then accepts the legal consents (best-effort) and navigates to the
 * onboarding wizard.
 *
 * Características:
 *   - RUES NIT lookup: al escribir NIT, debounce 600ms y autocompletar
 *     razón social + email corporativo desde RUES si existe.
 *   - TyC obligatorios pre-registro: checkboxes para Términos,
 *     Privacidad, Habeas Data y Mandato. Submit dispara consents.accept
 *     para los kinds aceptados. Cumplimiento Ley 1581/2012.
 *
 * Si el endpoint de register-organization retorna 409 (tax_id ya
 * registrado por otro user), mostramos error claro: debe pedir al owner
 * que lo invite.
 */

import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  ArrowRight,
  Building2,
  Check,
  ExternalLink,
  Loader2,
} from 'lucide-react'
import { toast } from 'sonner'

import {
  authApi,
  legalApi,
  lookupApi,
  setAccessToken,
  type RegisterOrgRequest,
} from '@/lib/api'
import { ROUTES } from '@/lib/constants'
import { AUTH_ME_QUERY_KEY } from '@/hooks/use-auth-context'

// Documentos legales que el user debe aceptar al registrar empresa.
// kind y label tienen que estar alineados con el backend (legal.py).
const LEGAL_DOCS = [
  {
    kind: 'terms',
    label: 'Términos y Condiciones',
    url: 'https://taxor.co/legal/terms',
  },
  {
    kind: 'privacy',
    label: 'Política de Privacidad',
    url: 'https://taxor.co/legal/privacy',
  },
  {
    kind: 'habeas_data',
    label: 'Autorización Habeas Data (Ley 1581/2012)',
    url: 'https://taxor.co/legal/habeas-data',
  },
  {
    kind: 'mandate',
    label: 'Contrato de Mandato (firma electrónica DIAN)',
    url: 'https://taxor.co/legal/mandate',
  },
] as const

export function RegisterCompanyPage() {
  const nav = useNavigate()
  const qc = useQueryClient()
  const [taxId, setTaxId] = useState('')
  const [taxIdDv, setTaxIdDv] = useState('')
  const [razonSocial, setRazonSocial] = useState('')
  const [nombreComercial, setNombreComercial] = useState('')
  const [emailCorporativo, setEmailCorporativo] = useState('')
  const [autoFilledFrom, setAutoFilledFrom] = useState<string | null>(null)
  const [acceptedDocs, setAcceptedDocs] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)
  // true cuando el 409 tax_id_already_registered: el bloque de error
  // ofrece además el link a login (el user probablemente ya fue invitado
  // o debe pedir invitación al owner).
  const [taxIdConflict, setTaxIdConflict] = useState(false)

  // J.6.5b — RUES NIT lookup debounced.
  const [lookingUp, setLookingUp] = useState(false)
  useEffect(() => {
    const clean = taxId.trim()
    setAutoFilledFrom(null)
    if (clean.length < 9) return
    const handle = setTimeout(async () => {
      setLookingUp(true)
      try {
        const r = await lookupApi.nit(clean)
        if (r.found) {
          // Solo autocompleta campos vacíos — no sobreescribe lo que el
          // user ya escribió a mano.
          if (r.razon_social && !razonSocial.trim()) {
            setRazonSocial(r.razon_social)
          }
          if (r.email_corporativo && !emailCorporativo.trim()) {
            setEmailCorporativo(r.email_corporativo)
          }
          setAutoFilledFrom('RUES')
        }
      } catch {
        // Silent: si RUES falla, el user llena a mano.
      } finally {
        setLookingUp(false)
      }
    }, 600)
    return () => clearTimeout(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxId])

  function toggleDoc(kind: string) {
    setAcceptedDocs((prev) => {
      const next = new Set(prev)
      if (next.has(kind)) next.delete(kind)
      else next.add(kind)
      return next
    })
  }

  const register = useMutation({
    mutationFn: async () => {
      setError(null)
      setTaxIdConflict(false)
      const body: RegisterOrgRequest = {
        tax_id: taxId.trim(),
        tax_id_dv: taxIdDv.trim() || undefined,
        razon_social: razonSocial.trim(),
        nombre_comercial: nombreComercial.trim() || undefined,
        email_corporativo: emailCorporativo.trim() || undefined,
      }
      // tax-account creates organization + membership + taxor.clients
      // atomically in a single cross-schema transaction, then reissues
      // a fresh JWT with active_org_id pointing to the new org.
      const orgResp = await authApi.registerOrganization(body)
      setAccessToken(orgResp.access_token)
      // Best-effort legal consents. A failure here does not block the
      // registration — the user can re-accept from /cuenta/privacidad.
      const consentResults = await Promise.allSettled(
        LEGAL_DOCS.filter((d) => acceptedDocs.has(d.kind)).map((d) =>
          legalApi.acceptConsent(d.kind),
        ),
      )
      const consentFailed = consentResults.filter((r) => r.status === 'rejected').length
      if (consentFailed > 0) {
        console.warn(`${consentFailed} consents fallaron — verifica en /cuenta/privacidad`)
      }
      return { orgResp, consentFailed }
    },
    onSuccess: () => {
      toast.success('Empresa registrada — completemos el onboarding')
      qc.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY })
      qc.invalidateQueries({ queryKey: ['me', 'consents'] })
      nav(ROUTES.WIZARD, { replace: true })
    },
    onError: (e: unknown) => {
      const err = e as {
        response?: { data?: { detail?: { message?: string; code?: string } } }
        message?: string
      }
      const apiMsg = err.response?.data?.detail?.message
      const apiCode = err.response?.data?.detail?.code
      if (apiCode === 'tax_id_already_registered') {
        setTaxIdConflict(true)
        setError(
          'Esta empresa ya está registrada en Taxor por otro usuario. ' +
            'Pídele al dueño (owner) de la organización que te invite como ' +
            'miembro desde su panel. Cuando aceptes la invitación, inicia ' +
            'sesión de nuevo para ver la empresa.',
        )
      } else if (apiMsg) {
        setError(apiMsg)
      } else {
        setError(err.message ?? 'Error registrando empresa.')
      }
    },
  })

  const allDocsAccepted = LEGAL_DOCS.every((d) => acceptedDocs.has(d.kind))
  const canSubmit =
    /^\d{9,12}$/.test(taxId.trim()) &&
    razonSocial.trim().length >= 3 &&
    razonSocial.trim().length <= 300 &&
    (taxIdDv.trim() === '' || /^\d$/.test(taxIdDv.trim())) &&
    allDocsAccepted &&
    !register.isPending

  return (
    <div className="min-h-screen bg-bg-app text-fg-primary">
      <header className="border-b border-border bg-bg-surface/80 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-4">
          <Link to={ROUTES.LOGIN} className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-brand-500 font-mono text-sm font-bold text-white">
              Tx
            </div>
            <span className="text-base font-semibold">Taxor Facturador</span>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-6 py-12">
        <div className="rounded-xl border border-border bg-bg-surface p-8 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-brand-500/10 text-brand-600">
              <Building2 className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Registra tu empresa</h1>
              <p className="mt-1 text-sm text-fg-secondary">
                Aún no tienes empresa asociada. Crea una para empezar a emitir
                facturas electrónicas DIAN.
              </p>
            </div>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (canSubmit) register.mutate()
            }}
            className="mt-8 space-y-5"
          >
            {error && (
              <div className="rounded-md border border-error/30 bg-error-tint p-3 text-sm text-error-fg flex items-start gap-2">
                <AlertCircle className="h-4 w-4 flex-none mt-0.5" />
                <div className="flex-1">
                  <span>{error}</span>
                  {taxIdConflict && (
                    <Link
                      to={ROUTES.LOGIN}
                      className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:underline"
                    >
                      Ir a iniciar sesión
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  )}
                </div>
              </div>
            )}

            <Field
              label="NIT *"
              help={
                lookingUp
                  ? 'Consultando RUES…'
                  : autoFilledFrom
                    ? `✓ Datos traídos de ${autoFilledFrom} — puedes editarlos`
                    : 'Sin DV — solo 9-12 dígitos. Si tu empresa está en RUES, autocompletamos.'
              }
            >
              <div className="relative">
                <input
                  type="text"
                  value={taxId}
                  onChange={(e) =>
                    setTaxId(e.target.value.replace(/\D/g, '').slice(0, 12))
                  }
                  inputMode="numeric"
                  autoComplete="off"
                  required
                  className={`${inputCls} font-mono`}
                  placeholder="900123456"
                />
                {lookingUp && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-fg-muted" />
                )}
              </div>
            </Field>

            <Field
              label="Dígito de verificación (DV)"
              help="Opcional. El último dígito calculado por DIAN."
            >
              <input
                type="text"
                value={taxIdDv}
                onChange={(e) =>
                  setTaxIdDv(e.target.value.replace(/\D/g, '').slice(0, 1))
                }
                maxLength={1}
                inputMode="numeric"
                className={`${inputCls} font-mono w-20`}
                placeholder="1"
              />
            </Field>

            <Field
              label="Razón social *"
              help="Nombre legal exacto como aparece en RUT."
            >
              <input
                type="text"
                value={razonSocial}
                onChange={(e) => setRazonSocial(e.target.value)}
                required
                minLength={3}
                maxLength={300}
                className={inputCls}
                placeholder="ACME COLOMBIA S.A.S."
              />
            </Field>

            <Field
              label="Nombre comercial"
              help="Opcional. Si difiere de la razón social."
            >
              <input
                type="text"
                value={nombreComercial}
                onChange={(e) => setNombreComercial(e.target.value)}
                maxLength={200}
                className={inputCls}
                placeholder="Acme"
              />
            </Field>

            <Field
              label="Email corporativo"
              help="Para notificaciones de facturas y alertas DIAN. Recomendado."
            >
              <input
                type="email"
                value={emailCorporativo}
                onChange={(e) => setEmailCorporativo(e.target.value)}
                className={inputCls}
                placeholder="facturacion@tuempresa.co"
              />
            </Field>

            {/* J.6.6 — TyC obligatorios */}
            <div className="rounded-md border border-border bg-bg-app p-4">
              <p className="text-sm font-medium mb-3">
                Antes de registrar, lee y acepta los términos:
              </p>
              <ul className="space-y-2">
                {LEGAL_DOCS.map((doc) => (
                  <li key={doc.kind} className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      id={`doc-${doc.kind}`}
                      checked={acceptedDocs.has(doc.kind)}
                      onChange={() => toggleDoc(doc.kind)}
                      className="mt-1"
                    />
                    <label
                      htmlFor={`doc-${doc.kind}`}
                      className="flex-1 text-sm cursor-pointer"
                    >
                      Acepto{' '}
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-600 hover:underline inline-flex items-center gap-0.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {doc.label}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </label>
                    {acceptedDocs.has(doc.kind) && (
                      <Check className="h-4 w-4 flex-none text-success-fg mt-0.5" />
                    )}
                  </li>
                ))}
              </ul>
              {!allDocsAccepted && (
                <p className="mt-3 text-xs text-warning-fg">
                  Debes aceptar los 4 documentos para registrar.
                </p>
              )}
            </div>

            <div className="flex items-center justify-between pt-2">
              <Link
                to={ROUTES.LOGIN}
                className="text-sm text-fg-secondary hover:text-fg-primary"
              >
                ← Salir
              </Link>
              <button
                type="submit"
                disabled={!canSubmit}
                className="inline-flex items-center gap-2 rounded-md bg-gradient-to-r from-brand-500 to-rose-500 px-5 py-2.5 text-sm font-medium text-white transition-all hover:shadow-lg hover:shadow-brand-500/30 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {register.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Registrando…
                  </>
                ) : (
                  <>
                    Registrar y continuar
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </div>
            {!canSubmit && !register.isPending && (
              <p className="text-xs text-fg-muted">
                Completa los campos marcados con * y acepta los términos.
              </p>
            )}
          </form>

          <p className="mt-6 text-xs text-fg-muted">
            Al registrar, te conviertes en <strong>owner</strong> de la empresa.
            Después podrás invitar a tu contador y otros miembros.
          </p>
        </div>
      </main>
    </div>
  )
}

const inputCls =
  'mt-1.5 w-full rounded-md border border-border bg-bg-app px-3 py-2.5 text-sm text-fg-primary outline-none transition-colors focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20'

function Field({
  label,
  help,
  children,
}: {
  label: string
  help?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="block text-xs font-medium uppercase tracking-wide text-fg-secondary">
        {label}
      </label>
      {children}
      {help && <p className="mt-1 text-xs text-fg-muted">{help}</p>}
    </div>
  )
}
